import * as React from 'react';
import Typography from '@mui/material/Typography';
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { Alert, Autocomplete, Button, FormControl, Grid, InputLabel, MenuItem, Modal, Select, TextField, Tooltip } from '@mui/material';
import * as Yup from 'yup';
import { Storefront, StorefrontCreationResponse, StorefrontResponse } from '../models/StorefrontResponse';
import { Formik } from 'formik';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { AutocompleteUser, useUsers } from '../hooks/useUsers';
import UserList from './UserList';
import { Geography } from '../models/DataSource';
import geographyData from '../assets/data/geography.json';
import { APIEndpoint, EndpointType, httpPost } from '../utils/apiService';

interface Props {
    storefront: Storefront
    closeDialogCallback(): void
}

export default function NewStoreDialog(props: Props) {
    const [openSelectUserDialog, setOpenSelectUserDialog] = React.useState(false);
    const [mainContact, setMainContact] = React.useState<string>("");
    const [saveError, setSaveError] = React.useState<string>("");
    const [geography, setGeography] = React.useState<Geography>({ state: {}, county: {}, metro_area: {}, scf: {} });
    const { users, autocompleteUsers } = useUsers(0);

    React.useEffect(() => {
        setGeography(geographyData as Geography);
    }, []);

    const validationSchema = Yup.object({
        storefrontName: Yup.string()
            .required("Storefront name is required")
            .matches(/^[^ ]+$/, 'No spaces allowed')
            .matches(/^[a-zA-Z0-9]+$/, 'Only alphanumeric characters are allowed'),
        title: Yup.string().required("Title is required"),
        minOrderAmount: Yup.number()
            .required("Minimum Order Amount is required")
            .positive("Minimum Order Amount should be a positive number")
            .integer("Minimum Order Amount should be an integer"),
        companyName: Yup.string().required("Company Name is required"),

        // Optional
        address: Yup.string().optional(),
        address2: Yup.string().optional(),
        city: Yup.string().optional(),
        state: Yup.string().optional(),
        zip: Yup.string().optional(),
        websiteURL: Yup.string()
            .url("Website URL is not valid")
            .optional(),
        phone: Yup.string()
            .matches(/^[0-9]+$/, "Phone number should contain only digits")
            .min(10, "Phone number should be at least 10 digits")
            .max(15, "Phone number should be at most 15 digits")
            .optional(),
    });

    const handleDialogClose = () => {
        props.closeDialogCallback()
    };

    const handleCloseSelectUserDialog = () => {
        setOpenSelectUserDialog(false);
    }

    const handleUserSelected = (userID: string) => {
        // Handle user selection
        //console.log("User selected:", userID);

        // Find the user in the list of users
        const selectedUser = users.find(user => user.id === userID);
        if (selectedUser) {
            console.log("Selected User:", selectedUser);
        }
        setOpenSelectUserDialog(false);
    }

    async function createStorefront(storefrontName: string, title: string, minOrderAmount: number,
        companyName: string, address: string, address2: string, city: string, state: string, zip: string, websiteURL: string, phone: string) {
        const request: StorefrontCreationResponse = {
            storeName: storefrontName,
            title: title,
            minOrderAmount: minOrderAmount,
            mainContact: mainContact,
            companyName: companyName,
            address: address,
            address2: address2,
            city: city,
            state: state,
            zip: zip,
            websiteURL: websiteURL,
            phone: phone,
        }

        setSaveError("");

        const apiURL = APIEndpoint(EndpointType.Storefront) + "/new"
        httpPost(apiURL, JSON.stringify(request))
            .then((data) => {
                const storefrontResponse = data as StorefrontResponse;

                if (storefrontResponse.status == 'error') {
                    console.log("Error saving storefront: " + storefrontResponse.errorMessage)
                    setSaveError("Error saving storefront: " + storefrontResponse.errorMessage)
                }
            })
            .catch((error) => {
                setSaveError("failed to create storefront: " + error.message)
            });
    }

    const mainContactChanged = (event: React.SyntheticEvent<Element, Event>, value: AutocompleteUser | null) => {
        if (value) {
            setMainContact(value.id);
        } else {
            setMainContact("");
        }
    };

    const dialogStyle = {
        position: 'absolute',
        top: '40%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 700,
        bgcolor: props.storefront.activeColors.mainPage.headerBackground,
        border: '1px solid #5c5c5e',
        boxShadow: 24,
        p: 1,
    };

    return (
        <Stack sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', ml: 0, mr: -1 }}>
                <Typography component="h2" variant="h6" color="primary" gutterBottom>
                    Create New Store
                </Typography>
                <IconButton
                    aria-label="close"
                    color="inherit"
                    onClick={() => handleDialogClose()}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Grid>
                <Formik
                    initialValues={{
                        storefrontName: "",
                        minOrderAmount: 300,
                        title: "Lead Shop",
                        phone: "",
                        companyName: "",
                        address: "",
                        address2: "",
                        city: "",
                        state: "",
                        zip: "",
                        websiteURL: "",
                        submit: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        values: any,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        { setErrors, setStatus, setSubmitting }: any
                    ): Promise<void> => {
                        try {
                            setSubmitting(true);
                            await createStorefront(values.storefrontName, values.title, values.minOrderAmount,
                                values.companyName, values.address, values.address2, values.city, values.state, values.zip, values.websiteURL, values.phone);
                                props.closeDialogCallback();
                            setSubmitting(false);
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } catch (err: any) {
                            setStatus({ success: false });
                            setErrors({ submit: err.message });
                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        errors,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        touched,
                        values,
                    }): JSX.Element => (
                        <Box
                            component="form"
                            noValidate
                            onSubmit={handleSubmit}
                            sx={{ mt: 3 }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Tooltip title="The storefront name is a one-word name (less than 10 characters) that will be used to identify the storefront and will appear in the URL for the store.">
                                        <TextField
                                            required
                                            fullWidth
                                            id="storefrontName"
                                            label="Storefront Name"
                                            name="storefrontName"
                                            value={values?.storefrontName.toLocaleLowerCase()}
                                            onChange={handleChange}
                                            error={Boolean(touched.storefrontName && errors.storefrontName)}
                                            helperText={touched.storefrontName && errors.storefrontName}
                                            autoComplete="storefrontName"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Tooltip title="The title appears at the top of the Lead Store and is used to identify the store.">
                                        <TextField
                                            required
                                            fullWidth
                                            id="title"
                                            label="Title"
                                            name="title"
                                            value={values?.title}
                                            onChange={handleChange}
                                            error={Boolean(touched.title && errors.title)}
                                            helperText={touched.title && errors.title}
                                            autoComplete="title"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="Select the main person to contact for this store.">
                                        <Autocomplete
                                            disablePortal
                                            autoFocus
                                            onChange={mainContactChanged}
                                            id="mainContact"
                                            isOptionEqualToValue={(
                                                option: AutocompleteUser,
                                                value: AutocompleteUser
                                            ) => option.id === value.id}
                                            options={autocompleteUsers}

                                            renderInput={(params) => (
                                                <TextField {...params}
                                                    label="Main Store Contact"
                                                    required
                                                    id="mainContact"
                                                    name="mainContact"
                                                    value={mainContact}
                                                    autoComplete="mainContact"
                                                    error={Boolean(mainContact === "")}
                                                />
                                            )}

                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="Name of the Company">
                                        <TextField
                                            required
                                            fullWidth
                                            id="companyName"
                                            label="Company Name"
                                            name="companyName"
                                            value={values?.companyName}
                                            onChange={handleChange}
                                            error={Boolean(touched.companyName && errors.companyName)}
                                            helperText={touched.companyName && errors.companyName}
                                            autoComplete="companyName"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="Address for the Company">
                                        <TextField
                                            fullWidth
                                            id="address"
                                            label="Address"
                                            name="address"
                                            value={values?.address}
                                            onChange={handleChange}
                                            error={Boolean(touched.address && errors.address)}
                                            helperText={touched.address && errors.address}
                                            autoComplete="address"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="Second part of the address for the Company">
                                        <TextField
                                            fullWidth
                                            id="address2"
                                            label="Address 2/Suite"
                                            name="address2"
                                            value={values?.address2}
                                            onChange={handleChange}
                                            error={Boolean(touched.address2 && errors.address2)}
                                            helperText={touched.address2 && errors.address2}
                                            autoComplete="address2"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Tooltip title="City of the Company">
                                        <TextField
                                            fullWidth
                                            id="city"
                                            label="City"
                                            name="city"
                                            value={values?.city}
                                            onChange={handleChange}
                                            error={Boolean(touched.city && errors.city)}
                                            helperText={touched.city && errors.city}
                                            autoComplete="city"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>State</InputLabel>
                                        <Select
                                            fullWidth
                                            id="state"
                                            name="state"
                                            label="State"
                                            value={values?.state}
                                            onChange={handleChange}
                                            error={Boolean(touched.state && errors.state)}
                                        >
                                            {Object.entries(geography.state).map(([key, value]) => (
                                                <MenuItem value={key} key={key}>{value}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Tooltip title="Zip code of the Company">
                                        <TextField
                                            fullWidth
                                            id="zip"
                                            label="Zip Code"
                                            name="zip"
                                            value={values?.zip}
                                            onChange={handleChange}
                                            error={Boolean(touched.zip && errors.zip)}
                                            helperText={touched.zip && errors.zip}
                                            autoComplete="zip"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Tooltip title="Company phone number">
                                        <TextField
                                            fullWidth
                                            id="phone"
                                            label="Company Phone"
                                            name="phone"
                                            value={values?.phone}
                                            onChange={handleChange}
                                            error={Boolean(touched.phone && errors.phone)}
                                            helperText={touched.phone && errors.phone}
                                            autoComplete="phone"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Tooltip title="The minimum order amount is the minimum amount that a customer must order to place an order.">
                                        <TextField
                                            required
                                            fullWidth
                                            id="minOrderAmount"
                                            label="Minimum Order Amount"
                                            name="minOrderAmount"
                                            value={values?.minOrderAmount}
                                            onChange={handleChange}
                                            error={Boolean(touched.minOrderAmount && errors.minOrderAmount)}
                                            helperText={touched.minOrderAmount && errors.minOrderAmount}
                                            autoComplete="minOrderAmount"
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="The website URL is needed for matching its color styles to the storefront.">
                                        <TextField
                                            fullWidth
                                            id="websiteURL"
                                            label="Company Website URL"
                                            name="websiteURL"
                                            value={values?.websiteURL}
                                            onChange={handleChange}
                                            error={Boolean(touched.websiteURL && errors.websiteURL)}
                                            helperText={touched.websiteURL && errors.websiteURL}
                                            autoComplete="websiteURL"
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            {/*Display Error with Icon*/}
                            {errors?.submit && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <ErrorOutlineIcon sx={{ color: "red", mr: 1 }} />
                                    <Typography variant="body2" color="error">
                                        {errors?.submit}
                                    </Typography>
                                </Box>
                            )}
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                Sign Up
                            </Button>
                        </Box>
                    )}
                </Formik>
                {saveError && saveError.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2" color="inherit">
                            {saveError}
                        </Typography>
                    </Alert>
                )}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="inherit">
                        Once the storefront is created, you will be able configure the logos.
                    </Typography>
                </Alert>
            </Grid>
            <Modal
                open={openSelectUserDialog}
                onClose={handleCloseSelectUserDialog}
            >
                <Box sx={dialogStyle}>
                    <UserList storefront={props.storefront} autocompleteUsers={autocompleteUsers} userSelectedCallback={handleUserSelected} closeDialogCallback={handleCloseSelectUserDialog} />
                </Box>
            </Modal>
        </Stack>

    );
}
